import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, CheckCircle, XCircle, Clock, MapPin, FileText, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_BASE}/api`;

// Format date to Indian style: DD-MM-YYYY
const formatIndianDate = (dateString) => {
  try {
    const date = parseISO(dateString);
    return format(date, 'dd-MM-yyyy');
  } catch {
    return dateString;
  }
};

// Format time to 12-hour format
const formatTimeRange = (timeSlot) => {
  try {
    const [start, end] = timeSlot.split(' - ');
    const formatTime = (timeString) => {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  } catch {
    return timeSlot;
  }
};

export default function DoctorAppointments() {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchAppointments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/appointments/doctor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppointments(response.data);
      setFilteredAppointments(response.data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await axios.patch(
        `${API}/appointments/${appointmentId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-bold text-background-dark">Appointments</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="appointment-filter" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredAppointments.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <p className="text-slate-600">No appointments found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAppointments.map((appt) => (
            <div
              key={appt.id}
              className="bg-white rounded-xl border border-slate-200 p-6"
              data-testid={`appointment-${appt.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-heading font-bold text-lg text-background-dark">
                      {appt.mr_details?.full_name}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        appt.status === 'approved'
                          ? 'bg-accent/10 text-accent'
                          : appt.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-destructive/10 text-destructive'
                      }`}
                    >
                      {appt.status.toUpperCase()}
                    </span>
                  </div>

                  {appt.mr_details?.company_name && (
                    <div className="mb-3 text-sm">
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                        {appt.mr_details.company_name}
                        {appt.mr_details.division && ` - ${appt.mr_details.division}`}
                      </span>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span className="font-medium">{formatIndianDate(appt.appointment_date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">{formatTimeRange(appt.time_slot)}</span>
                    </div>
                  </div>

                  {appt.notes && (
                    <div className="mt-3 text-sm text-slate-600">
                      <span className="font-medium">Notes:</span> {appt.notes}
                    </div>
                  )}

                  <div className="mt-3 text-sm text-slate-600">
                    <span className="font-medium">Contact:</span> {appt.mr_details?.phone}
                  </div>

                  {appt.mr_details?.product_brochure && (
                    <div className="mt-3">
                      <a
                        href={appt.mr_details.product_brochure}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium"
                      >
                        <FileText className="h-4 w-4" />
                        View Product Brochure
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>

                {appt.status === 'pending' && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      data-testid={`approve-appointment-${appt.id}`}
                      size="sm"
                      onClick={() => handleUpdateStatus(appt.id, 'approved')}
                      className="rounded-full bg-accent hover:bg-accent/90"
                    >
                      <CheckCircle className="mr-1 h-4 w-4" />
                      Approve
                    </Button>
                    <Button
                      data-testid={`cancel-appointment-${appt.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(appt.id, 'cancelled')}
                      className="rounded-full border-destructive text-destructive hover:bg-destructive/10"
                    >
                      <XCircle className="mr-1 h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}